import { api } from './apiClient';

// Create lesson content
export const createLessonContent = async (payload) => {
  try {
    const response = await api.post('/api/v1/admin/lesson-content', payload);
    return response.data;
  } catch (error) {
    if (error.response) {
      const message = error.response.data?.message || `Server error: ${error.response.status}`;
      throw new Error(message);
    } else if (error.request) {
      throw new Error('Unable to reach the server. Please check your internet connection or try again later.');
    }
    throw new Error(error.message || 'An unexpected error occurred');
  }
};



// List lesson content
export const fetchLessonContentList = async (lessonId, page = 1, page_size = 15, search = '', filters = {}) => {
  try {
    const body = {
      lesson_id: lessonId,
      page,
      // Send multiple common pagination keys for backend compatibility.
    
      limit: page_size,
      pageSize: page_size,
      search,
      filters,
    };
    const response = await api.post('/api/v1/admin/lesson-content/list', body);
    return response.data;
  } catch (error) {
    if (error.response) {
      const message = error.response.data?.message || `Server error: ${error.response.status}`;
      throw new Error(message);
    } else if (error.request) {
      throw new Error('Unable to reach the server. Please check your internet connection or try again later.');
    }
    throw new Error(error.message || 'An unexpected error occurred');
  }
};

// Update lesson content
export const updateLessonContent = async (contentId, payload) => {
  if (!contentId) {
    throw new Error('Content ID is required to update lesson content');
  }

  if (!payload) {
    throw new Error('Payload is required to update lesson content');
  }

  try {
    const response = await api.put(`/api/v1/admin/lesson-content/${contentId}`, payload);
    return response.data;
  } catch (error) {
    if (error.response) {
      const message = error.response.data?.message || `Server error: ${error.response.status}`;
      throw new Error(message);
    } else if (error.request) {
      throw new Error('Unable to reach the server. Please check your internet connection or try again later.');
    }
    throw new Error(error.message || 'An unexpected error occurred');
  }
};

// Delete lesson content
export const deleteLessonContent = async (contentId) => {
  if (!contentId) {
    throw new Error('Content ID is required to delete lesson content');
  }

  try {
    const response = await api.delete(`/api/v1/admin/lesson-content/${contentId}`);
    return response.data;
  } catch (error) {
    if (error.response) {
      const message = error.response.data?.message || `Server error: ${error.response.status}`;
      throw new Error(message);
    } else if (error.request) {
      throw new Error('Unable to reach the server. Please check your internet connection or try again later.');
    }
    throw new Error(error.message || 'An unexpected error occurred');
  }
};

// --- Lesson CRUD (Containers for Lesson Content) ---

// Fetch lesson list
export const fetchLessonList = async (chapterId, page = 1, limit = 10, searchtext = '', filters = {}) => {
  if (!chapterId) {
    throw new Error('Chapter ID is required to fetch lessons');
  }
  try {
    const body = {
      chapter_id: chapterId,
      page,
      limit,
      searchtext: searchtext || '',
      filters,
    };
    const response = await api.post('/api/v1/admin/lessons/list', body);
    return response.data;
  } catch (error) {
    if (error.response) {
      const message = error.response.data?.message || `Server error: ${error.response.status}`;
      throw new Error(message);
    } else if (error.request) {
      throw new Error('Unable to reach the server. Please check your internet connection or try again later.');
    }
    throw new Error(error.message || 'An unexpected error occurred');
  }
};

// Create lesson
export const createLesson = async (payload) => {
  try {
    const response = await api.post('/api/v1/admin/lessons', payload);
    return response.data;
  } catch (error) {
    if (error.response) {
      const message = error.response.data?.message || `Server error: ${error.response.status}`;
      throw new Error(message);
    } else if (error.request) {
      throw new Error('Unable to reach the server. Please check your internet connection or try again later.');
    }
    throw new Error(error.message || 'An unexpected error occurred');
  }
};

// Update lesson
export const updateLesson = async (payload) => {
  try {
    const response = await api.put('/api/v1/admin/lessons', payload);
    return response.data;
  } catch (error) {
    if (error.response) {
      const message = error.response.data?.message || `Server error: ${error.response.status}`;
      throw new Error(message);
    } else if (error.request) {
      throw new Error('Unable to reach the server. Please check your internet connection or try again later.');
    }
    throw new Error(error.message || 'An unexpected error occurred');
  }
};

// Delete lesson
export const deleteLesson = async (lessonId) => {
  if (!lessonId) {
    throw new Error('Lesson ID is required to delete lesson');
  }
  try {
    const response = await api.delete(`/api/v1/admin/lessons/${lessonId}`);
    return response.data;
  } catch (error) {
    if (error.response) {
      const message = error.response.data?.message || `Server error: ${error.response.status}`;
      throw new Error(message);
    } else if (error.request) {
      throw new Error('Unable to reach the server. Please check your internet connection or try again later.');
    }
    throw new Error(error.message || 'An unexpected error occurred');
  }
};

// --- CSV Operations ---

// Upload lesson CSV
export const uploadLessonCsv = async (file, contentType) => {

  if (!file) {
    throw new Error('CSV file is required');
  }

  try {
    const formData = new FormData();
    formData.append('file', file);
    if (contentType) {
      formData.append('content_type', contentType);
    }

    const response = await api.post('/api/v1/admin/lessons/upload-csv', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  } catch (error) {
    if (error.response) {
      const message = error.response.data?.message || `Server error: ${error.response.status}`;
      throw new Error(message);
    } else if (error.request) {
      throw new Error('Unable to reach the server. Please check your internet connection or try again later.');
    }
    throw new Error(error.message || 'An unexpected error occurred');
  }
};

// Download lesson CSV template
export const downloadLessonCsvTemplate = async () => {
  try {
    const response = await api.get('/api/v1/admin/lessons/csv-template', {
      responseType: 'blob',
    });

    // Create a link element, set its href to the blob URL, and trigger a click
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'lesson_template.csv');
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
  } catch (error) {
    if (error.response) {
      const message = error.response.data?.message || `Server error: ${error.response.status}`;
      throw new Error(message);
    } else if (error.request) {
      throw new Error('Unable to reach the server. Please check your internet connection or try again later.');
    }
    throw new Error(error.message || 'An unexpected error occurred');
  }
};